import React, { useState, useEffect } from 'react';
import { toast } from '../../lib/toast';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdUmschalter, LdFensterFuss } from '../../components/ld/LdFenster';
import { ausflugApi, AusflugTermin, MahnungEmpfaenger, MahnungAusgenommen, MahnungDraft } from './ausflug-api';

const GRUND_LABEL: Record<MahnungAusgenommen['grund'], string> = {
  abgelehnt:        'abgelehnt',
  sensible_woerter: 'sensible Notiz',
  notiz_vorhanden:  'Notiz vorhanden',
  mahnung_max:      'schon zweimal erinnert',
};

// Welle K4: „Eltern erinnern“ — ein Fenster; oben die Wahl Zustimmung oder Zahlung (mit Anzahl offen)
export function MahnungModal({ terminId, termin, typ: startTyp, offen, onClose, onSent }: {
  terminId: number;
  termin: AusflugTermin;
  typ: 'zustimmung' | 'zahlung';
  offen?: { zustimmung: number; zahlung: number };
  onClose: () => void;
  onSent: () => void;
}) {
  const [typ, setTyp] = useState(startTyp);
  const [laden, setLaden]   = useState(true);
  const [senden, setSenden] = useState(false);
  const [inkludiert, setInkludiert]   = useState<MahnungEmpfaenger[]>([]);
  const [ausgenommen, setAusgenommen] = useState<MahnungAusgenommen[]>([]);
  const [drafts, setDrafts] = useState<MahnungDraft[] | null>(null);
  const [vorschau, setVorschau] = useState<{ schueler_name: string; betreff: string; body: string } | null>(null);

  useEffect(() => {
    setLaden(true);
    ausflugApi.mahnungPreview(terminId, typ)
      .then(d => { setInkludiert(d.empfaenger_inkludiert); setAusgenommen(d.ausgenommen); setVorschau(d.vorschau ?? null); })
      .catch(err => toast(err.message ?? 'Vorschau fehlgeschlagen.', 'error'))
      .finally(() => setLaden(false));
  }, [terminId, typ]);

  async function senden_() {
    setSenden(true);
    try {
      const ids = inkludiert.map(e => e.schueler_id);
      const res = await ausflugApi.mahnungBulk(terminId, typ, ids);
      if (res.drafts_zum_kopieren.length > 0) {
        setDrafts(res.drafts_zum_kopieren);
        if (res.gesendet_count > 0) toast(`${res.gesendet_count} gesendet, ${res.drafts_zum_kopieren.length} zum Kopieren`, 'info');
        else toast('Gmail nicht verfügbar, bitte Texte selbst senden', 'info');
      } else {
        toast(`${res.gesendet_count} ${res.gesendet_count === 1 ? 'Erinnerung' : 'Erinnerungen'} gesendet`, 'success');
        onSent();
      }
    } catch (err: any) {
      toast(err.message ?? 'Senden fehlgeschlagen.', 'error');
    } finally {
      setSenden(false);
    }
  }

  function kopiere(text: string) {
    navigator.clipboard?.writeText(text).then(
      () => toast('Text kopiert', 'success'),
      () => toast('Kopieren fehlgeschlagen', 'error'),
    );
  }

  const fuss = drafts
    ? <LdFensterFuss onAbbrechen={onSent} abbrechen="Fertig" />
    : <LdFensterFuss onAbbrechen={onClose} haupt={`Erinnerung senden (${inkludiert.length})`} onHaupt={senden_} laden={senden} aus={laden || inkludiert.length === 0} />;

  return (
    <LdSheet open onClose={onClose} maxWidth={560} kopf={{ titel: 'Eltern erinnern', kontext: termin.titel, icon: 'mail' }} fuss={fuss}>
      {!drafts && (
        <LdFeld label="Woran erinnern?">
          <LdUmschalter aria-label="Woran erinnern?" wert={typ} onChange={setTyp}
            optionen={[
              { wert: 'zustimmung', label: `Zustimmung${offen ? ` (${offen.zustimmung})` : ''}` },
              { wert: 'zahlung', label: `Zahlung${offen ? ` (${offen.zahlung})` : ''}` },
            ]} />
        </LdFeld>
      )}

      {laden ? (
        <p className="ld-fe-text" role="status">Lädt …</p>
      ) : drafts ? (
        // ── Texte zum Kopieren ──
        <>
          <p className="ld-fe-text">Gmail ist nicht verbunden. Kopiere die Texte und sende sie über dein E-Mail-Programm.</p>
          {drafts.map(d => (
            <div key={d.schueler_id} className="ld-af-mail">
              <b>{d.schueler_name}</b>
              {d.empfaenger.length > 0 && <small>an {d.empfaenger.join(', ')}</small>}
              <pre>Betreff: {d.betreff}{'\n\n'}{d.mail_text}</pre>
              <button type="button" className="ld-kl-link" onClick={() => kopiere(`Betreff: ${d.betreff}\n\n${d.mail_text}`)}>Text kopieren</button>
            </div>
          ))}
        </>
      ) : (
        // ── Vorschau ──
        <>
          <LdFeld label={`Empfänger (${inkludiert.length})`}>
            {inkludiert.length === 0 ? (
              <p className="ld-fe-text">Niemand zu erinnern.</p>
            ) : (
              <ul className="ld-af-empf">
                {inkludiert.map(e => (
                  <li key={e.schueler_id}>
                    <b>{e.schueler_name}</b>
                    <small className={e.eltern_hauptkontakte.length ? undefined : 'ist-fehlt'}>
                      {e.eltern_hauptkontakte.length ? e.eltern_hauptkontakte.map(k => k.email).join(', ') : 'kein Hauptkontakt hinterlegt'}
                    </small>
                  </li>
                ))}
              </ul>
            )}
          </LdFeld>

          {ausgenommen.length > 0 && (
            <LdFeld label={`Nicht erinnert (${ausgenommen.length})`}>
              <ul className="ld-af-empf">
                {ausgenommen.map(a => <li key={a.schueler_id}><b>{a.schueler_name}</b><small>{GRUND_LABEL[a.grund]}</small></li>)}
              </ul>
            </LdFeld>
          )}

          {/* Welle Z3: genau der Text vom Server (Beispiel: erster Empfänger) */}
          {vorschau && (
            <LdFeld label="So sieht die Mail aus" hinweis={`Beispiel für ${vorschau.schueler_name}`}>
              <pre className="ld-af-vorschau">{`Betreff: ${vorschau.betreff}

${vorschau.body}`}</pre>
            </LdFeld>
          )}
        </>
      )}
    </LdSheet>
  );
}
